import { useState, useRef, useImperativeHandle, forwardRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, X, Loader2, ImagePlus, PenLine } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { processImageFile, processImageFilesBatch } from "@/lib/imageProcessing";
import { useQueryClient } from "@tanstack/react-query";
import { useBadgeCheck } from "@/hooks/useBadges";
import { autoSelectCoverPhoto } from "@/lib/autoCoverPhoto";
import { invalidateCafeData, invalidateCafePhotos } from "@/lib/queryKeys";
import type { PhotoCategory } from "@/components/PhotoCategorySelector";
export type { PhotoCategory };
import posthog from "posthog-js";

export interface PhotoUploadRef {
  triggerFileSelect: () => void;
}

interface PhotoUploadProps {
  cafeId: string;
  cafeName?: string;
  onUploadComplete?: (photoUrl: string, photoId: string) => void;
  className?: string;
  compact?: boolean;
  category?: PhotoCategory;
  categories?: PhotoCategory[];
  checkInId?: string;
  isAdminUpload?: boolean;
}

interface QueuedPhoto {
  id: string;
  preview: string;
  file: File;
  caption: string;
  status: "pending" | "uploading" | "done" | "error";
}

export const PhotoUpload = forwardRef<PhotoUploadRef, PhotoUploadProps>(function PhotoUpload({ cafeId, cafeName, onUploadComplete, className, compact = false, category, categories, checkInId, isAdminUpload = false }, ref) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [queue, setQueue] = useState<QueuedPhoto[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { checkBadges } = useBadgeCheck();

  useImperativeHandle(ref, () => ({
    triggerFileSelect: () => fileInputRef.current?.click(),
  }));

  // Clean up object URLs on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      queue.forEach((p) => {
        if (p.preview.startsWith("blob:")) {
          URL.revokeObjectURL(p.preview);
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setIsProcessing(true);
    try {
      const fileArray = Array.from(files);
      await processImageFilesBatch(
        fileArray,
        {},
        (result, index) => {
          // Validate the processed file isn't empty/corrupted
          if (result.file.size < 100) {
            toast({
              title: "Image error",
              description: `${fileArray[index].name}: Image appears to be empty or corrupted after processing`,
              variant: "destructive",
            });
            return;
          }
          // Progressive: add each photo as soon as it's ready
          const newPhoto: QueuedPhoto = {
            id: `${Date.now()}-${index}`,
            preview: result.preview,
            file: result.file,
            caption: "",
            status: "pending",
          };
          setQueue((prev) => [...prev, newPhoto]);
        },
        2 // concurrency
      );
    } finally {
      setIsProcessing(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const updateCaption = (id: string, caption: string) => {
    setQueue((prev) => prev.map((p) => (p.id === id ? { ...p, caption } : p)));
  };

  const removePhoto = (id: string) => {
    setQueue((prev) => {
      const photo = prev.find((p) => p.id === id);
      if (photo?.preview.startsWith("blob:")) {
        URL.revokeObjectURL(photo.preview);
      }
      return prev.filter((p) => p.id !== id);
    });
  };

  const handleUploadAll = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) {
      toast({
        title: "Sign in required",
        description: "Please sign in to upload photos",
        variant: "destructive",
        action: <ToastAction altText="Sign In" onClick={() => navigate("/auth", { state: { from: window.location.pathname } })}>Sign In</ToastAction>,
      });
      return;
    }

    setIsUploading(true);
    let uploadedCount = 0;
    let lastPhotoUrl = "";
    let lastPhotoId = "";

    for (const photo of queue) {
      if (photo.status === "done") continue;

      setQueue((prev) => prev.map((p) => (p.id === photo.id ? { ...p, status: "uploading" } : p)));

      try {
        const fileExt = photo.file.name.split(".").pop();
        const fileName = `${session.user.id}/${cafeId}/${Date.now()}-${photo.id}.${fileExt}`;

        const { error: uploadError } = await supabase.storage
          .from("cafe-images")
          .upload(fileName, photo.file);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from("cafe-images")
          .getPublicUrl(fileName);

        const { data: photoData, error: dbError } = await supabase
          .from("cafe_photos")
          .insert({
            cafe_id: cafeId,
            user_id: session.user.id,
            photo_url: publicUrl,
            caption: photo.caption.trim() || null,
            category: category || null,
            categories: categories && categories.length > 0 ? categories : null,
            check_in_id: checkInId || null,
            is_admin_upload: isAdminUpload,
          })
          .select()
          .single();

        if (dbError) throw dbError;

        setQueue((prev) => prev.map((p) => (p.id === photo.id ? { ...p, status: "done" } : p)));
        uploadedCount++;
        lastPhotoUrl = publicUrl;
        lastPhotoId = photoData.id;

        try {
          posthog.capture('photo_uploaded', { cafe_id: cafeId, cafe_name: cafeName ?? null });
        } catch { /* ignore */ }

        // Fire-and-forget: AI quality scoring
        supabase.functions.invoke('score-photo', {
          body: { photo_url: publicUrl, photo_id: photoData.id },
        }).catch((err) => console.warn('Photo scoring skipped:', err));
      } catch (error: any) {
        console.error("Upload error:", error);
        setQueue((prev) => prev.map((p) => (p.id === photo.id ? { ...p, status: "error" } : p)));
      }
    }

    if (uploadedCount > 0) {
      toast({
        title: uploadedCount === 1 ? "Photo uploaded!" : `${uploadedCount} photos uploaded!`,
        description: "Thanks for sharing — your photo is now live!",
      });

      // Clear completed photos
      setQueue((prev) => prev.filter((p) => p.status !== "done"));

      try {
        await autoSelectCoverPhoto(cafeId);
      } catch (e) {
        console.warn("Auto cover selection failed:", e);
      }
      invalidateCafePhotos(queryClient, cafeId);
      invalidateCafeData(queryClient, cafeId);

      onUploadComplete?.(lastPhotoUrl, lastPhotoId);

      // Check for photographer badge
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      if (currentSession?.user) {
        checkBadges(currentSession.user.id, 'photo', cafeId);
      }
    }

    setIsUploading(false);
  };

  const clearAll = () => {
    queue.forEach((p) => {
      if (p.preview.startsWith("blob:")) {
        URL.revokeObjectURL(p.preview);
      }
    });
    setQueue([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const pendingCount = queue.filter((p) => p.status === "pending" || p.status === "error").length;

  // Compact mode: just a button
  if (compact && queue.length === 0) {
    return (
      <div className={className}>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileSelect}
          className="hidden"
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          disabled={isProcessing}
          className="gap-2"
        >
          {isProcessing ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Camera className="h-4 w-4" />
          )}
          {isProcessing ? "Processing..." : "Add Photos"}
        </Button>
      </div>
    );
  }

  return (
    <div className={cn("space-y-3", className)}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileSelect}
        className="hidden"
      />

      {queue.length === 0 ? (
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={isProcessing}
          className={cn(
            "w-full border-2 border-dashed border-border rounded-xl p-6",
            "flex flex-col items-center justify-center gap-2",
            "text-muted-foreground hover:text-foreground hover:border-primary/50",
            "transition-colors duration-200 cursor-pointer"
          )}
        >
          {isProcessing ? (
            <>
              <Loader2 className="h-8 w-8 animate-spin" />
              <span className="text-sm font-medium">Processing images...</span>
            </>
          ) : (
            <>
              <ImagePlus className="h-8 w-8" />
              <span className="text-sm font-medium">Add photos</span>
              <span className="text-xs text-muted-foreground">Select multiple • JPG, PNG up to 10MB</span>
            </>
          )}
        </button>
      ) : (
        <div className="space-y-3">
          {/* Photo grid */}
          <div className={cn(
            "grid gap-3",
            queue.length === 1 ? "grid-cols-1" : "grid-cols-2"
          )}>
            {queue.map((photo) => (
              <div key={photo.id} className="relative rounded-xl overflow-hidden bg-muted/30">
                <img
                  src={photo.preview}
                  alt="Preview"
                  className={cn(
                    "w-full object-cover mx-auto",
                    queue.length === 1 ? "max-h-64" : "h-32"
                  )}
                />
                {/* Status overlay */}
                {photo.status === "uploading" && (
                  <div className="absolute inset-0 bg-background/70 flex items-center justify-center">
                    <Loader2 className="h-6 w-6 animate-spin" />
                  </div>
                )}
                {photo.status === "done" && (
                  <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                    <span className="text-xs font-medium text-primary bg-background/90 px-2 py-1 rounded-full">✓ Done</span>
                  </div>
                )}
                {photo.status === "error" && (
                  <div className="absolute inset-0 bg-destructive/20 flex items-center justify-center">
                    <span className="text-xs font-medium text-destructive bg-background/90 px-2 py-1 rounded-full">Failed</span>
                  </div>
                )}
                {/* Remove button */}
                {photo.status !== "uploading" && (
                  <button
                    type="button"
                    onClick={() => removePhoto(photo.id)}
                    className="absolute top-1.5 right-1.5 p-1 rounded-full bg-background/90 hover:bg-background transition-colors"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
                {/* Add caption button */}
                {photo.status !== "uploading" && photo.status !== "done" && !photo.caption && (
                  <button
                    type="button"
                    onClick={() => updateCaption(photo.id, " ")}
                    className="absolute bottom-1.5 left-1.5 p-1 rounded-full bg-background/90 hover:bg-background transition-colors"
                    aria-label="Add caption"
                  >
                    <PenLine className="h-3.5 w-3.5 text-muted-foreground" />
                  </button>
                )}
                {/* Inline caption indicator */}
                {photo.caption.trim() && photo.status !== "done" && (
                  <div className="absolute bottom-0 left-0 right-0 bg-background/80 backdrop-blur-sm px-2 py-1">
                    <p className="text-[10px] text-foreground truncate">{photo.caption.trim()}</p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Per-photo captions */}
          {queue.some(p => (p.caption === " " || p.caption.trim() !== "") && p.status !== "done") && (
            <div className="space-y-2">
              {queue.map((photo, idx) => {
                const isEditing = (photo.caption === " " || photo.caption.trim() !== "") && photo.status !== "done";
                if (!isEditing) return null;
                return (
                  <div key={photo.id} className="flex items-center gap-2">
                    <img
                      src={photo.preview}
                      alt=""
                      className="w-8 h-8 rounded object-cover flex-shrink-0"
                    />
                    <Input
                      value={photo.caption === " " ? "" : photo.caption}
                      onChange={(e) => updateCaption(photo.id, e.target.value || "")}
                      placeholder={`Caption for photo ${idx + 1} (optional)`}
                      maxLength={200}
                      className="h-8 text-xs"
                      autoFocus={photo.caption === " "}
                      onBlur={() => {
                        if (photo.caption === " ") updateCaption(photo.id, "");
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => updateCaption(photo.id, "")}
                      className="p-1 text-muted-foreground hover:text-foreground flex-shrink-0"
                      aria-label="Remove caption"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={isProcessing || isUploading}
              className="gap-1.5"
            >
              <ImagePlus className="h-4 w-4" />
              Add More
            </Button>
            <div className="flex-1" />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={clearAll}
              disabled={isUploading}
            >
              Clear
            </Button>
            <Button
              type="button"
              onClick={handleUploadAll}
              disabled={isUploading || pendingCount === 0}
              size="sm"
              className="gap-1.5"
            >
              {isUploading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Camera className="h-4 w-4" />
                  Upload {pendingCount > 1 ? `${pendingCount} Photos` : "Photo"}
                </>
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
});
